import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { transactionDetails, transactionLines, transactionSummary, purchaseAmount, purchaseDate } from "../../../inventory_comps/exports/SalesPDFExport.jsx";
import { purchaseLines, purchaseSummary } from "../../../inventory_comps/exports/OrdersPDFExport.jsx";
import ReportPDF from "../../reports_exportations/ReportsPDFExport.jsx";
import "./reportRecordModal.css";

const show = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "object") return "Not recorded";
  return String(value);
};
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const statusOf = record => typeof record?.status === "object" ? record.status?.current : record?.status;
const historyOf = record => Array.isArray(record?.status?.history) ? record.status.history :
  Array.isArray(record?.statusHistory) ? record.statusHistory : Array.isArray(record?.auditTrail) ? record.auditTrail : [];

function recordView(selected, columns) {
  const record = selected.record || {};
  const kind = selected.recordKind;
  const isPurchase = ["order", "receive", "purchaseReturn"].includes(kind);
  const purchaseKind = kind === "purchaseReturn" ? "return" : kind;
  const isDirectory = ["items", "contacts"].includes(kind);
  const summary = isPurchase ? purchaseSummary(record, purchaseKind) :
    !isDirectory ? transactionSummary(record, kind) : null;
  const date = summary?.date || record.createdAt || record.audit?.createdAt;
  const money = summary?.amount === null || summary?.amount === undefined ? null :
    purchaseAmount(summary.amount, summary.currency || selected.currency || "NGN");

  const primary = (columns || []).map((label, index) => [label, show(selected.cells?.[index])]);
  const extra = isPurchase ? [
    ["Purchase order", summary.orderId],
    ["Supplier", summary.supplier],
    ["Supplier ID", record.supplier?.supplierId],
    ["Supplier email", record.supplier?.email],
    ["Supplier telephone", record.supplier?.phone || record.supplier?.contact],
    ["Warehouse / destination", summary.warehouse],
    ["Expected delivery", record.delivery?.expectedDeliveryDate ? purchaseDate(record.delivery.expectedDeliveryDate) : null],
    ["Actual delivery", record.delivery?.actualDeliveryDate ? purchaseDate(record.delivery.actualDeliveryDate) : null],
    ["Receive reference", record.receiveId],
    ["Processed by", record.receivedBy?.name || record.processedBy?.name],
    ["Recorded lines", summary.count], ["Recorded units", summary.quantity],
  ].filter(([,v]) => hasValue(v)) :
    isDirectory ? (selected.details || []) : transactionDetails(record, kind);
  const lines = isPurchase ? purchaseLines(record, purchaseKind).map(line => ({...line,
    more: purchaseKind === "return" ? line.reason : purchaseKind === "receive" ?
      `Rejected: ${show(line.rejectedQty)}` : `Received: ${show(line.receivedQty)}`
  })) : isDirectory ? [] : transactionLines(record, kind).map(line => ({...line,
    more: [line.details, hasValue(line.delta) ? `Change: ${line.delta}` : null,
      hasValue(line.before) ? `Stock: ${line.before} → ${show(line.after)}` : null].filter(Boolean).join(" · ")
  }));
  const linkedSales = Array.isArray(selected.sales) ? selected.sales : [];
  return {kind, record, primary, extra, lines, linkedSales, summary, isPurchase, purchaseKind,
    history:historyOf(record), date:date ? purchaseDate(date) : null, money,
    status:show(summary?.status || statusOf(record) || (kind === "items" ? selected.cells?.[4] : kind === "contacts" ? selected.cells?.[2] : null)),
    currency:summary?.currency || selected.currency || "NGN"};
}

export default function ReportRecordModal({ selected, definition, report, onDismiss }) {
  const company = useSelector(state => state.clientFunction?.companiesDB?.clientData);
  const printableRef = useRef(null);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const content = useMemo(() => recordView(selected, report.columns), [selected, report.columns]);
  const printRecord = useReactToPrint({contentRef:printableRef, documentTitle:String(selected.id || definition.title)});
  const pdfRecord = useMemo(() => ({
    title:`${definition.title} — ${selected.id}`,
    columns:report.columns,
    rows:[selected],
    metrics:content.extra.map(([label,value])=>[label,show(value)]),
    movements:[],
  }),[definition.title, selected, report.columns, content.extra]);
  useEffect(() => {
    const before = document.activeElement;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const onKey = event => {
      if (event.key === "Escape") {event.preventDefault();event.stopPropagation();onDismiss();}
      if (event.key !== "Tab") return;
      const tabbable = [...(dialogRef.current?.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),[tabindex]:not([tabindex="-1"])') || [])];
      if (!tabbable.length) return;
      if (event.shiftKey && document.activeElement === tabbable[0]) {event.preventDefault();tabbable[tabbable.length-1].focus();}
      else if (!event.shiftKey && document.activeElement === tabbable[tabbable.length-1]) {event.preventDefault();tabbable[0].focus();}
    };
    document.addEventListener("keydown",onKey);
    return () => {document.body.style.overflow = oldOverflow;document.removeEventListener("keydown",onKey);before?.focus?.();};
  },[onDismiss]);
  const companyName = company?.businessProfile?.basicInfo?.businessName;
  const extraUnique = content.extra.filter(([label])=>!content.primary.some(([main])=>main.toLowerCase()===String(label).toLowerCase()));
  const dateLine = [content.date, content.summary?.count != null ? `${content.summary.count} line items` : null].filter(Boolean).join("  •  ");

  return createPortal(
    <div className="report-record-overlay" onMouseDown={event=>{if(event.target===event.currentTarget)onDismiss();}}>
      <section ref={dialogRef} className="report-record-modal" role="dialog" aria-modal="true" aria-labelledby="report-record-modal-title" aria-describedby="report-record-modal-hint">
        <header className="report-record-modal-header">
          <div className="report-record-modal-heading">
            <span className="report-record-kicker">UNIVERSE INVENTORY · RECORD DETAILS</span>
            <h2 id="report-record-modal-title">{definition.title}</h2>
            <p id="report-record-modal-hint">View and print the information recorded for this report entry.</p>
          </div>
          <button ref={closeRef} type="button" className="report-record-close" onClick={onDismiss} aria-label="Close record details" title="Close details">×</button>
        </header>
        <div className="report-record-body">
          <article ref={printableRef} className="report-record-document">
            <div className="report-record-document-top">
              <div className="report-record-brand">
                <span className="report-record-brand-mark" aria-hidden="true">UI</span>
                <div><strong>{companyName || "Universe Inventory"}</strong><span>BUSINESS RECORD · {definition.title.toUpperCase()}</span></div>
              </div>
              <span className="report-record-reference">Reference <strong>{show(selected.id)}</strong></span>
            </div>
            <div className="report-record-intro">
              <div><span className="report-record-overline">SAVED REPORT ENTRY</span>
                <h3>{show(selected.cells?.[1]) !== "Not recorded" ? show(selected.cells?.[1]) : show(selected.id)}</h3>
                {dateLine&&<p>{dateLine}</p>}
              </div>
              <span className="report-record-status" title={content.status}>{content.status}</span>
            </div>
            {content.money&&<div className="report-record-value"><span>Recorded value</span><strong>{content.money}</strong></div>}
            <section className="report-record-section" aria-label="Record information">
              <div className="report-record-section-heading"><span className="report-record-section-number">01</span><h4>Record information</h4></div>
              <dl className="report-record-facts">{content.primary.map(([label,value],index)=><div key={`${label}-${index}`}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
            </section>
            {extraUnique.length>0&&<section className="report-record-section" aria-label="Additional information">
              <div className="report-record-section-heading"><span className="report-record-section-number">02</span><h4>Additional details</h4></div>
              <dl className="report-record-facts">{extraUnique.map(([label,value],index)=><div key={`${label}-${index}`}><dt>{label}</dt><dd>{show(value)}</dd></div>)}</dl>
            </section>}
            {content.lines.length>0&&<section className="report-record-section" aria-label="Associated line items">
              <div className="report-record-section-heading"><span className="report-record-section-number">03</span><h4>Recorded items <span>({content.lines.length})</span></h4></div>
              <div className="report-record-table-scroll"><table className="report-record-lines"><thead><tr>
                <th scope="col">Item / SKU</th><th scope="col">Qty</th><th scope="col">Unit price / cost</th><th scope="col">Recorded value</th><th scope="col">Batch</th><th scope="col">Details</th>
              </tr></thead><tbody>{content.lines.map((line,i)=><tr key={`${line.sku}-${i}`}>
                <td><strong>{show(line.name)}</strong><span>{show(line.sku)}</span></td>
                <td>{show(line.quantity)}</td><td>{purchaseAmount(line.price,content.currency)}</td>
                <td>{purchaseAmount(line.total,content.currency)}</td><td>{show(line.batch)}</td><td>{show(line.more)}</td>
              </tr>)}</tbody></table></div>
            </section>}
            {content.linkedSales.length>0&&<section className="report-record-section" aria-label="Saved customer sales">
              <div className="report-record-section-heading"><span className="report-record-section-number">03</span><h4>Associated sales <span>({content.linkedSales.length})</span></h4></div>
              <div className="report-record-table-scroll"><table className="report-record-lines"><thead><tr><th scope="col">Reference</th><th scope="col">Date</th><th scope="col">Status</th><th scope="col">Recorded value</th></tr></thead><tbody>
                {content.linkedSales.map((sale,index)=>{const summary=transactionSummary(sale,"sale");return <tr key={`${summary.id}-${index}`}><td>{show(summary.id)}</td><td>{purchaseDate(summary.date)}</td><td>{show(summary.status)}</td><td>{purchaseAmount(summary.amount,summary.currency)}</td></tr>;})}
              </tbody></table></div>
            </section>}
            {content.history.length>0&&<section className="report-record-section" aria-label="Saved record history">
              <div className="report-record-section-heading"><span className="report-record-section-number">04</span><h4>Recorded history</h4></div>
              <div className="report-record-history">{content.history.map((entry,index)=><div key={index}>
                <strong>{show(entry.state||entry.status||entry.action)}</strong>
                <span>{entry.timestamp||entry.createdAt?purchaseDate(entry.timestamp||entry.createdAt):"Not recorded"}</span>
                <span>{show(entry.userId||entry.by||entry.actor?.name)}</span>
              </div>)}</div>
            </section>}
            <footer className="report-record-document-footer"><span>Universe Inventory · Saved business information</span><span>{show(selected.id)}</span></footer>
          </article>
        </div>
        <footer className="report-record-modal-actions">
          <span className="report-record-action-note">Record details · {definition.title}</span>
          <div className="report-record-action-buttons">
            <button type="button" className="report-record-button report-record-button-muted" onClick={onDismiss}>Close</button>
            <ReportPDF report={pdfRecord} label="Export PDF"/>
            <button type="button" className="report-record-button report-record-button-primary" onClick={printRecord}>Print details</button>
          </div>
        </footer>
      </section>
    </div>,document.body
  );
}
